import React, { useState, useContext, useEffect, useMemo, useCallback } from "react";
import clsx from "clsx";

import {
  makeStyles,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  MenuItem,
  IconButton,
  Menu,
  useTheme,
  useMediaQuery,
  Avatar,
  Badge,
  withStyles,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  InputAdornment,
} from "@material-ui/core";

// Ícones do header — biblioteca lucide-react (padrão do projeto)
import {
  Menu as CustomMenuIcon,
  PanelRightOpen,
  PanelRightClose,
  RefreshCw as RefreshIcon,
  Download as InstallIcon,
  MoreVertical as MoreVertIcon,
  ChevronDown as ChevronDownIcon,
  UserCog as UserCogIcon,
  LogOut as LogOutIcon,
  Search as SearchIcon,
} from "lucide-react";

import { useSearch } from "../context/Search/SearchContext";

import MainListItems from "./MainListItems";
import NotificationsVolume from "../components/NotificationsVolume";
import UserModal from "../components/UserModal";
import { AuthContext } from "../context/Auth/AuthContext";
import BackdropLoading from "../components/BackdropLoading";
import { i18n } from "../translate/i18n";
import toastError from "../errors/toastError";
import UnifiedNotifications from "../components/UnifiedNotifications";

import { useDate } from "../hooks/useDate";
import UserLanguageSelector from "../components/UserLanguageSelector";

import ChatiaPopover from "../components/ChatiaPopover";
import ThemeToggle from "../components/ThemeToggle";
import ColorModeContext from "./themeContext";
import { getBackendUrl } from "../config";
import useSettings from "../hooks/useSettings";
import VersionControl from "../components/VersionControl";
import CalendarReminderListener from "../components/CalendarReminderListener";

// logos (fallbacks)
const logo = "/logo-light.png";
const logoDark = "/logo-dark.png";

const backendUrl = getBackendUrl();
const drawerWidth = 250;
const drawerWidthCollapsed = 80;
const TRIAL_WHATSAPP_DEFAULT_PHONE = "5581989974426";

const buildPublicAssetUrl = (value, fallback) => {
  const normalizedValue =
    typeof value === "string"
      ? value
      : value && typeof value === "object" && typeof value.value === "string"
        ? value.value
        : "";
  if (!normalizedValue) return fallback;
  if (normalizedValue.startsWith("http") || normalizedValue.startsWith("/")) return normalizedValue;
  return `${backendUrl}/public/${normalizedValue}`;
};

const TRIAL_WHATSAPP_TEXTS = {
  pt: { label: "Falar no WhatsApp", message: "Olá! Estou testando o ChatIA e preciso de ajuda." },
  en: { label: "Contact on WhatsApp", message: "Hi. I am testing ChatIA and need help." },
  es: { label: "Hablar por WhatsApp", message: "Hola. Estoy probando ChatIA y necesito ayuda." },
  tr: { label: "WhatsApp ile konuş", message: "Merhaba. ChatIA'yı test ediyorum ve yardıma ihtiyacım var." },
  ar: { label: "تواصل عبر واتساب", message: "مرحباً. أختبر ChatIA وأحتاج إلى مساعدة." },
};

const normalizeLayoutLanguage = (lang) => {
  const normalized = String(lang || "pt").toLowerCase().split("-")[0];
  return ["pt", "en", "es", "tr", "ar"].includes(normalized) ? normalized : "pt";
};

const tLayout = (key, fallback) => {
  const value = i18n.t(key);
  return !value || value === key ? fallback : value;
};

const normalizeWhatsAppPhone = (phone) =>
  String(phone || "").replace(/\D/g, "");

const isTrialPlan = (user) => {
  const trial = user?.company?.plan?.trial;
  if (trial === true || trial === "true" || trial === 1 || trial === "1") {
    return true;
  }
  return String(user?.company?.plan?.name || "")
    .toLowerCase()
    .includes("trial");
};

const WhatsAppIcon = (props) => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="currentColor"
    aria-hidden="true"
    {...props}
  >
    <path d="M20.52 3.48A11.74 11.74 0 0 0 12.06 0C5.44 0 .06 5.37.06 11.99c0 2.11.55 4.17 1.6 5.99L0 24l6.2-1.63a11.98 11.98 0 0 0 5.86 1.49h.01c6.62 0 11.99-5.37 11.99-11.99 0-3.2-1.25-6.2-3.54-8.39Zm-8.46 18.4h-.01a9.92 9.92 0 0 1-5.06-1.39l-.36-.22-3.68.97.98-3.58-.24-.37a9.95 9.95 0 0 1-1.52-5.28C2.17 6.1 6.49 1.79 11.99 1.79c2.64 0 5.12 1.03 6.77 2.9a9.5 9.5 0 0 1 2.57 6.6c0 5.5-4.32 10.59-9.27 10.59Zm5.39-7.35c-.29-.15-1.74-.85-2.01-.95-.27-.1-.47-.15-.67.15-.2.29-.77.95-.95 1.14-.18.2-.35.22-.64.07-.29-.15-1.22-.45-2.32-1.43-.86-.77-1.43-1.72-1.6-2-.17-.29-.02-.44.13-.58.13-.13.29-.35.44-.53.15-.18.2-.29.29-.48.1-.2.05-.37-.02-.53-.07-.15-.67-1.62-.92-2.22-.24-.58-.49-.5-.67-.51l-.57-.01c-.2 0-.53.07-.81.37-.29.29-1.09 1.06-1.09 2.57 0 1.51 1.11 2.96 1.26 3.16.15.2 2.17 3.31 5.25 4.64.73.31 1.3.5 1.75.64.74.24 1.41.2 1.94.12.59-.09 1.74-.71 1.99-1.39.24-.67.24-1.24.17-1.39-.07-.15-.27-.22-.57-.37Z" />
  </svg>
);

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    height: "100dvh",
    [theme.breakpoints.down("sm")]: {
      height: "100dvh",
    },
    background: theme.mode === "light" ? "#ffffff" : "#121212",
    "& .MuiButton-outlinedPrimary": {
      color: theme.palette.primary,
      border:
        theme.mode === "light"
          ? "1px solid rgba(0 124 102)"
          : "1px solid rgba(255, 255, 255, 0.5)",
    },
    "& .MuiTab-textColorPrimary.Mui-selected": {
      color: theme.palette.primary,
    },
  },

  chip: { background: "red", color: "white" },
  avatar: { width: "100%" },

  toolbar: {
    paddingRight: 24,
    color: theme.mode === "light" ? "#1a1a1a" : "#e9edef",
    background: "transparent",
    gap: theme.spacing(1),
    overflow: "visible",
    position: "relative",
    zIndex: 1,
    [theme.breakpoints.down("sm")]: {
      paddingRight: theme.spacing(1),
      paddingLeft: theme.spacing(1),
      minHeight: 48,
      gap: theme.spacing(0.5),
      display: "flex",
      alignItems: "center",
      flexWrap: "nowrap",
    },
  },

  // SCROLLER HORIZONTAL (ícones)
  topbarScroller: {
    display: "flex",
    alignItems: "center",
    gap: 15,
    flex: "1 1 0%",
    minWidth: 0,
    maxWidth: "100%",
    flexWrap: "nowrap",

    // DESKTOP: alinhar à direita
    justifyContent: "flex-end",
    overflowX: "visible",

    // cada filho não encolhe => gera overflow quando somar mais que a largura
    "& > *": { flex: "0 0 auto" },

    // MOBILE: alinhar à direita + scroll horizontal invisível
    [theme.breakpoints.down("sm")]: {
      justifyContent: "flex-end",
      gap: 2,
      overflowX: "auto",
      overflowY: "hidden",
      WebkitOverflowScrolling: "touch",
      touchAction: "pan-x",
      overscrollBehaviorX: "contain",
      msOverflowStyle: "none",
      scrollbarWidth: "none",
      "&::-webkit-scrollbar": { display: "none" },
    },
  },

  toolbarIcon: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundSize: "cover",
    padding: "0 8px",
    height: 64,
    minHeight: 64,
    [theme.breakpoints.down("sm")]: { height: 64 },
  },

  appBar: {
    zIndex: theme.zIndex.drawer - 1,
    marginLeft: drawerWidthCollapsed + 16,
    width: `calc(100% - ${drawerWidthCollapsed + 16}px)`,
    transition: theme.transitions.create(["width", "margin"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    overflow: "hidden",
    background: theme.mode === "light" ? "#ffffff" : "#121212",
    boxShadow: "none",
    color: theme.mode === "light" ? "#1a1a1a" : "#e9edef",
    [theme.breakpoints.down("sm")]: {
      marginLeft: 0,
      width: "100%",
    },
  },
  "@keyframes headerBubbleFloat1": {
    "0%":   { transform: "translateX(0) translateY(0)" },
    "50%":  { transform: "translateX(80px) translateY(-8px)" },
    "100%": { transform: "translateX(0) translateY(0)" },
  },
  "@keyframes headerBubbleFloat2": {
    "0%":   { transform: "translateX(0) translateY(0)" },
    "50%":  { transform: "translateX(-60px) translateY(6px)" },
    "100%": { transform: "translateX(0) translateY(0)" },
  },
  "@keyframes headerBubbleFloat3": {
    "0%":   { transform: "translateX(0) scale(1)" },
    "50%":  { transform: "translateX(50px) scale(1.2)" },
    "100%": { transform: "translateX(0) scale(1)" },
  },
  headerBubblesWrap: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    pointerEvents: "none",
    overflow: "hidden",
    zIndex: 0,
  },
  hBubble: {
    position: "absolute",
    borderRadius: "50%",
    background: "rgba(255,255,255,0.22)",
  },
  hb1: { width: 18, height: 18, top: 6, left: "5%", animation: "$headerBubbleFloat1 12s ease-in-out infinite" },
  hb2: { width: 10, height: 10, top: 20, left: "15%", animation: "$headerBubbleFloat2 9s ease-in-out infinite" },
  hb3: { width: 14, height: 14, top: 4, left: "30%", animation: "$headerBubbleFloat3 14s ease-in-out infinite" },
  hb4: { width: 8, height: 8, top: 22, left: "50%", animation: "$headerBubbleFloat1 10s ease-in-out infinite reverse" },
  hb5: { width: 12, height: 12, top: 8, left: "65%", animation: "$headerBubbleFloat2 11s ease-in-out infinite reverse" },
  hb6: { width: 16, height: 16, top: 3, left: "80%", animation: "$headerBubbleFloat3 13s ease-in-out infinite" },
  hb7: { width: 6, height: 6, top: 26, left: "92%", animation: "$headerBubbleFloat1 8s ease-in-out infinite" },
  appBarShift: {
    marginLeft: drawerWidth + 16,
    width: `calc(100% - ${drawerWidth + 16}px)`,
    transition: theme.transitions.create(["width", "margin"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
    [theme.breakpoints.down("sm")]: {
      marginLeft: 0,
      width: "100%",
    },
  },

  title: {
    flexGrow: 0,
    fontSize: 14,
    color: theme.mode === "light" ? "#1a1a1a" : "#e9edef",
    marginLeft: theme.spacing(1),
    [theme.breakpoints.down("sm")]: { display: "none" },
  },
  globalSearchWrap: {
    flex: 1,
    display: "flex",
    justifyContent: "center",
    padding: "0 24px",
    minWidth: 0,
  },
  globalSearchInput: {
    maxWidth: 460,
    width: "100%",
    "& .MuiOutlinedInput-root": {
      borderRadius: 999,
      backgroundColor: theme.mode === "dark" ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.035)",
      "& fieldset": {
        borderColor: theme.mode === "dark" ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.06)",
      },
      "&:hover fieldset": {
        borderColor: theme.mode === "dark" ? "rgba(255,255,255,0.12)" : "rgba(0,0,0,0.12)",
      },
      "&.Mui-focused fieldset": {
        borderColor: theme.palette.primary.main,
        borderWidth: 1,
      },
    },
    "& .MuiOutlinedInput-input": {
      padding: "7px 10px",
      fontSize: 13,
      color: theme.mode === "dark" ? "#e9edef" : "#1a1a1a",
    },
  },

  // Sidebar flutuante glass — container vertical solto, blur + borda translúcida.
  drawerPaper: {
    position: "fixed",
    top: 12,
    left: 12,
    bottom: 12,
    whiteSpace: "nowrap",
    width: drawerWidth,
    height: "calc(100vh - 24px)",
    transition: "width 200ms ease-in-out",
    overflowX: "hidden",
    overflowY: "hidden",
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText || "#e9edef",
    borderRadius: 12,
    border: "1px solid rgba(255,255,255,0.10)",
    backdropFilter: "blur(20px)",
    WebkitBackdropFilter: "blur(20px)",
    boxShadow: theme.mode === "light"
      ? "0 12px 36px rgba(0,0,0,0.10), 0 4px 12px rgba(0,0,0,0.06)"
      : "0 12px 36px rgba(0,0,0,0.45), 0 4px 12px rgba(0,0,0,0.30)",
    zIndex: theme.zIndex.drawer + 2,
    display: "flex",
    flexDirection: "column",
    [theme.breakpoints.down("sm")]: {
      top: 0,
      left: 0,
      bottom: 0,
      height: "100vh",
      borderRadius: 0,
    },
  },
  drawerPaperClose: {
    width: drawerWidthCollapsed,
    overflowX: "hidden",
    overflowY: "hidden",
    transition: "width 200ms ease-in-out",
    // borderRadius herdado do drawerPaper — fica preservado quando colapsado
  },

  appBarSpacer: {
    minHeight: 48,
  },

  content: {
    flex: 1,
    overflow: "hidden",
    position: "relative",
    display: "flex",
    flexDirection: "column",
    // compensa a sidebar fixed (largura + margens)
    marginLeft: drawerWidthCollapsed + 16,
    transition: theme.transitions.create("margin-left", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    [theme.breakpoints.down("sm")]: {
      marginLeft: 0,
    },
  },
  contentShift: {
    marginLeft: drawerWidth + 16,
    transition: theme.transitions.create("margin-left", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
    [theme.breakpoints.down("sm")]: {
      marginLeft: 0,
    },
  },

  container: {
    paddingTop: theme.spacing(4),
    paddingBottom: theme.spacing(4),
  },

  containerWithScroll: {
    flex: 1,
    overflowY: "auto",
    overflowX: "hidden",
    padding: "4px 0",
    ...theme.scrollbarStyles,
    "&::-webkit-scrollbar": { width: 4 },
  },

  logoImg: {
    width: "auto",
    height: 32,
    maxWidth: 140,
    objectFit: "contain",
  },
  sidebarCollapsedIcon: {
    width: 34,
    height: 34,
    objectFit: "contain",
    display: "block",
  },
  hideLogo: { display: "none" },

  avatar2: {
    width: theme.spacing(4),
    height: theme.spacing(4),
    cursor: "pointer",
    borderRadius: "50%",
    border: "none",
  },

  userTriggerBtn: {
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    padding: "4px 10px 4px 4px",
    borderRadius: 999,
    cursor: "pointer",
    background: theme.mode === "dark"
      ? "rgba(255,255,255,0.06)"
      : "rgba(0,0,0,0.04)",
    color: theme.mode === "dark" ? "#e9edef" : "#1a1a1a",
    transition: "background 0.15s ease, filter 0.15s",
    border: "none",
    "&:hover": {
      background: theme.mode === "dark"
        ? "rgba(255,255,255,0.10)"
        : "rgba(0,0,0,0.07)",
    },
  },
  userTriggerName: {
    fontSize: 13,
    fontWeight: 600,
    color: theme.mode === "dark" ? "#e9edef" : "#111b21",
    maxWidth: 140,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    [theme.breakpoints.down("sm")]: { display: "none" },
  },
  userTriggerAvatar: {
    width: 30,
    height: 30,
    border: "none",
  },
  userPopoverItem: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    padding: "10px 12px",
    borderRadius: 8,
    cursor: "pointer",
    transition: "background 0.15s ease",
    color: theme.mode === "dark" ? "#e9edef" : "#111b21",
    "&:hover": {
      background: theme.mode === "dark"
        ? "rgba(255,255,255,0.06)"
        : "rgba(0,0,0,0.04)",
    },
  },
  userPopoverItemDanger: {
    color: "#e53935",
    "&:hover": {
      background: "rgba(229, 57, 53, 0.08)",
    },
  },
  userPopoverHeader: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    padding: "12px",
    borderBottom: theme.mode === "dark"
      ? "1px solid rgba(255,255,255,0.06)"
      : "1px solid rgba(0,0,0,0.06)",
    marginBottom: 4,
  },
  userPopoverHeaderName: {
    fontSize: 14,
    fontWeight: 600,
    color: theme.mode === "dark" ? "#e9edef" : "#111b21",
    lineHeight: 1.2,
  },
  userPopoverHeaderEmail: {
    fontSize: 12,
    color: theme.mode === "dark" ? "#8696a0" : "#667781",
  },
  userPopoverDivider: {
    height: 1,
    background: theme.mode === "dark"
      ? "rgba(255,255,255,0.06)"
      : "rgba(0,0,0,0.06)",
    margin: "4px 0",
  },

  dueBadge: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    padding: "3px 10px",
    marginLeft: 8,
    borderRadius: 999,
    fontSize: 11,
    fontWeight: 600,
    lineHeight: 1.4,
    background: theme.mode === "light"
      ? `color-mix(in srgb, ${theme.palette.primary.main} 15%, transparent)`
      : `color-mix(in srgb, ${theme.palette.primary.main} 28%, transparent)`,
    color: theme.mode === "light"
      ? theme.palette.primary.main
      : `color-mix(in srgb, ${theme.palette.primary.main} 35%, #ffffff)`,
    border: theme.mode === "light"
      ? `1px solid color-mix(in srgb, ${theme.palette.primary.main} 25%, transparent)`
      : `1px solid color-mix(in srgb, ${theme.palette.primary.main} 45%, transparent)`,
    whiteSpace: "nowrap",
    verticalAlign: "middle",
  },
  dueBadgeDot: {
    width: 6,
    height: 6,
    borderRadius: "50%",
    background: theme.mode === "light"
      ? theme.palette.primary.main
      : `color-mix(in srgb, ${theme.palette.primary.main} 35%, #ffffff)`,
    flexShrink: 0,
  },

  compressIconButton: {
    [theme.breakpoints.down("sm")]: { padding: 6 },
  },

  moreMenuItem: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    padding: "9px 12px",
    borderRadius: 8,
    cursor: "pointer",
    transition: "all 0.15s ease",
    "&:hover": {
      background: theme.mode === "dark"
        ? "rgba(255,255,255,0.06)"
        : "rgba(0,0,0,0.04)",
    },
    // sobrescreve cor dos ícones filhos
    "& .MuiIconButton-root": {
      color: theme.mode === "dark" ? "#fff" : "#555",
      padding: 6,
    },
    "& svg": {
      stroke: theme.mode === "dark" ? "#fff" : "#555",
    },
  },
  moreMenuLabel: {
    fontSize: 14,
    fontWeight: 500,
    color: theme.mode === "dark" ? "#ffffff" : "#1a1a1a",
    flex: 1,
  },
  trialWhatsappButton: {
    position: "fixed",
    right: 22,
    bottom: 22,
    zIndex: theme.zIndex.drawer + 5,
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    minHeight: 44,
    padding: "10px 16px",
    borderRadius: 999,
    background: "#25D366",
    color: "#ffffff",
    fontSize: 13,
    fontWeight: 700,
    lineHeight: 1,
    textDecoration: "none",
    border: "1px solid rgba(255,255,255,0.22)",
    boxShadow: "0 14px 36px rgba(37, 211, 102, 0.35)",
    transition: "transform 0.16s ease, filter 0.16s ease",
    "&:hover": {
      color: "#ffffff",
      textDecoration: "none",
      transform: "translateY(-1px)",
      filter: "brightness(0.96)",
    },
    [theme.breakpoints.down("sm")]: {
      right: 12,
      bottom: 14,
      padding: "10px 12px",
      fontSize: 12,
    },
  },
  trialWhatsappIcon: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
}));

const StyledBadge = withStyles((theme) => ({
  badge: {
    backgroundColor: "#44b700",
    color: "#44b700",
    boxShadow: `0 0 0 2px ${theme.palette.background.paper}`,
    "&::after": {
      position: "absolute",
      top: 0,
      left: 0,
      width: "100%",
      height: "100%",
      borderRadius: "50%",
      animation: "$ripple 1.2s infinite ease-in-out",
      border: "1px solid currentColor",
      content: '""',
    },
  },
  "@keyframes ripple": {
    "0%": { transform: "scale(.8)", opacity: 1 },
    "100%": { transform: "scale(2.4)", opacity: 0 },
  },
}))(Badge);

const LoggedInLayout = ({ children }) => {
  const classes = useStyles();
  const [userToken, setUserToken] = useState("disabled");
  const [userModalOpen, setUserModalOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const { handleLogout, loading } = useContext(AuthContext);
  const [drawerOpen, setDrawerOpen] = useState(() => {
    const saved = localStorage.getItem("drawerOpen");
    if (saved !== null) return saved === "true";
    return document.body.offsetWidth > 600;
  });
  const [drawerVariant, setDrawerVariant] = useState("permanent");
  const [collapsedHeaderHover, setCollapsedHeaderHover] = useState(false);
  const { user, socket } = useContext(AuthContext);

  const theme = useTheme();
  const { colorMode } = useContext(ColorModeContext);
  const greaterThenSm = useMediaQuery(theme.breakpoints.up("sm"));
  const { ticketSearchParam, setTicketSearchParam } = useSearch();
  const searchPlaceholder = tLayout("mainDrawer.appBar.search", "Buscar...");

  const [moreMenuEl, setMoreMenuEl] = useState(null);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isAppInstalled, setIsAppInstalled] = useState(false);
  const [volume, setVolume] = useState(localStorage.getItem("volume") || 1);
  const { dateToClient } = useDate();
  const [profileUrl, setProfileUrl] = useState(null);
  const [trialWhatsAppEnabled, setTrialWhatsAppEnabled] = useState(true);
  const [trialWhatsAppPhone, setTrialWhatsAppPhone] = useState(
    TRIAL_WHATSAPP_DEFAULT_PHONE
  );

  // PWA install prompt
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
  useEffect(() => {
    if (window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone) {
      setIsAppInstalled(true);
    }
    const handleBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    const handleAppInstalled = () => {
      setIsAppInstalled(true);
      setDeferredPrompt(null);
    };
    window.addEventListener("beforeinstallprompt", handleBeforeInstall);
    window.addEventListener("appinstalled", handleAppInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", handleBeforeInstall);
      window.removeEventListener("appinstalled", handleAppInstalled);
    };
  }, []);

  const [showPWAInstructions, setShowPWAInstructions] = useState(false);

  const handleInstallPWA = useCallback(async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === "accepted") setIsAppInstalled(true);
      setDeferredPrompt(null);
      setMoreMenuEl(null);
    } else {
      setShowPWAInstructions(true);
      setMoreMenuEl(null);
    }
  }, [deferredPrompt]);

  const mainListItems = useMemo(
    () => <MainListItems drawerOpen={drawerOpen} collapsed={!drawerOpen} />,
    [user, drawerOpen]
  );

  const settings = useSettings();

  useEffect(() => {
    const getSetting = async () => {
      // Não fazer requisição se não houver usuário autenticado
      if (!user || !user.id) return;

      try {
        const response = await settings.get("wtV");
        setUserToken("disabled");
      } catch (error) {
        // Ignora erros de autenticação durante logout
        if (error?.response?.status !== 401) {
          console.error("Erro ao buscar setting wtV:", error);
        }
      }
    };
    getSetting();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Array vazio = executa apenas uma vez

  useEffect(() => {
    if (!user?.id) return;
    let cancelled = false;
    const loadTrialWhatsAppSettings = async () => {
      try {
        const [enabled, phone] = await Promise.all([
          settings.getPublicSetting("whatsappTrialButtonEnabled"),
          settings.getPublicSetting("whatsappTrialButtonPhone"),
        ]);
        if (cancelled) return;
        setTrialWhatsAppEnabled(enabled ? enabled === "enabled" : true);
        setTrialWhatsAppPhone(
          normalizeWhatsAppPhone(phone) || TRIAL_WHATSAPP_DEFAULT_PHONE
        );
      } catch {
        if (!cancelled) {
          setTrialWhatsAppEnabled(true);
          setTrialWhatsAppPhone(TRIAL_WHATSAPP_DEFAULT_PHONE);
        }
      }
    };
    loadTrialWhatsAppSettings();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, user?.companyId]);

  useEffect(() => {
    // Aplica defaultMenu só se o usuário nunca alterou manualmente
    if (document.body.offsetWidth > 600 && localStorage.getItem("drawerOpen") === null) {
      if (user.defaultMenu === "closed") setDrawerOpen(false);
      else setDrawerOpen(true);
    }
    if (user.defaultTheme === "dark" && theme.mode === "light") {
      colorMode.toggleColorMode();
    }
  }, [user.defaultMenu]);

  useEffect(() => {
    if (document.body.offsetWidth < 600) setDrawerVariant("temporary");
    else setDrawerVariant("permanent");
  }, [drawerOpen]);

  useEffect(() => {
    const companyId = user.companyId;
    const userId = user.id;
    if (companyId) {
      const ImageUrl = user.profileImage;
      if (ImageUrl) {
        // se já vem como URL absoluta, usa direto
        setProfileUrl(
          /^https?:\/\//i.test(ImageUrl)
            ? ImageUrl
            : `${backendUrl}/public/avatar/${ImageUrl}`
        );
      } else {
        // CRA não expõe process.env.FRONTEND_URL no browser (precisaria
        // de prefixo REACT_APP_), e o nopicture.png é servido na raiz.
        setProfileUrl(`/nopicture.png`);
      }

      const onCompanyAuthLayout = (data) => {
        if (data.user.id === +userId) {
          toastError("Sua conta foi acessada em outro computador.");
          setTimeout(() => {
            localStorage.clear();
            window.location.reload();
          }, 1000);
        }
      };

      socket.on(`company-${companyId}-auth`, onCompanyAuthLayout);

      socket.emit("userStatus");
      const interval = setInterval(() => {
        socket.emit("userStatus");
      }, 1000 * 60 * 5);

      return () => {
        socket.off(`company-${companyId}-auth`, onCompanyAuthLayout);
        clearInterval(interval);
      };
    }
  }, [socket]);

  const handleMenu = (event) => {
    setAnchorEl(event.currentTarget);
    setMenuOpen(true);
  };
  const handleCloseMenu = () => {
    setAnchorEl(null);
    setMenuOpen(false);
  };
  const handleOpenUserModal = () => {
    setUserModalOpen(true);
    handleCloseMenu();
  };
  const handleClickLogout = () => {
    handleCloseMenu();
    handleLogout();
  };
  const drawerClose = () => {
    if (document.body.offsetWidth < 600) {
      setDrawerOpen(false);
    }
  };
  if (loading) return <BackdropLoading />;

  const currentMode = colorMode?.mode || theme.mode || theme.palette?.type || "light";
  const logoSrc =
    currentMode === "dark"
      ? buildPublicAssetUrl(colorMode?.appLogoDark, logoDark)
      : buildPublicAssetUrl(colorMode?.appLogoLight, logo);
  const sidebarCollapsedIconSrc =
    currentMode === "dark"
      ? buildPublicAssetUrl(colorMode?.appSidebarIconDark, "/favicon.png")
      : buildPublicAssetUrl(colorMode?.appSidebarIconLight, "/favicon.png");
  const trialLanguage = normalizeLayoutLanguage(
    user?.language || localStorage.getItem("i18nextLng")
  );
  const trialWhatsAppText =
    TRIAL_WHATSAPP_TEXTS[trialLanguage] || TRIAL_WHATSAPP_TEXTS.pt;
  const normalizedTrialWhatsAppPhone =
    normalizeWhatsAppPhone(trialWhatsAppPhone) || TRIAL_WHATSAPP_DEFAULT_PHONE;
  const showTrialWhatsAppButton =
    isTrialPlan(user) && trialWhatsAppEnabled && normalizedTrialWhatsAppPhone;
  const trialWhatsAppUrl = `https://wa.me/${normalizedTrialWhatsAppPhone}?text=${encodeURIComponent(
    trialWhatsAppText.message
  )}`;
  const sidebarTextColor = theme.chatiaDS?.sidebar?.textColor || "#ffffff";

  return (
    <div className={classes.root}>
      <CalendarReminderListener />
      <Drawer
        variant={drawerVariant}
        PaperProps={{ elevation: 0 }}
        classes={{
          paper: clsx(classes.drawerPaper, !drawerOpen && classes.drawerPaperClose),
        }}
        open={drawerOpen}
      >
        <div
          className={classes.toolbarIcon}
          style={drawerOpen ? { justifyContent: "space-between" } : { justifyContent: "center" }}
          onMouseEnter={() => !drawerOpen && setCollapsedHeaderHover(true)}
          onMouseLeave={() => !drawerOpen && setCollapsedHeaderHover(false)}
        >
          {drawerOpen && (
            <img
              src={logoSrc}
              alt="logo"
              className={classes.logoImg}
              style={{ display: "block", margin: "0 auto", flex: 1 }}
            />
          )}
          {drawerOpen || collapsedHeaderHover ? (
            <IconButton
              onClick={() => { const next = !drawerOpen; setDrawerOpen(next); localStorage.setItem("drawerOpen", String(next)); setCollapsedHeaderHover(false); }}
              style={{
                flexShrink: 0,
                color: sidebarTextColor,
                padding: 8,
                width: 36,
                height: 36,
              }}
            >
              {drawerOpen ? (
                <PanelRightOpen size={20} strokeWidth={1.5} />
              ) : (
                <PanelRightClose size={20} strokeWidth={1.5} />
              )}
            </IconButton>
          ) : (
            <img
              src={sidebarCollapsedIconSrc}
              alt="sidebar icon"
              className={classes.sidebarCollapsedIcon}
            />
          )}
        </div>
        <List className={classes.containerWithScroll} style={{ flex: 1 }}>
          <MainListItems collapsed={!drawerOpen} />
        </List>
        <Divider
          style={{
            backgroundColor: sidebarTextColor,
            opacity: theme.mode === "dark" ? 0.12 : 0.16,
          }}
        />
      </Drawer>

      <AppBar
        position="fixed"
        className={clsx(classes.appBar, drawerOpen && classes.appBarShift)}
        color="default"
        elevation={0}
      >
        <Toolbar variant="dense" className={classes.toolbar}>
          {/* Hamburger menu (mobile apenas) */}
          {!greaterThenSm && (
            <IconButton
              color="inherit"
              onClick={() => { setDrawerOpen(true); }}
              style={{ padding: 8, marginLeft: 4, marginRight: 4 }}
            >
              <CustomMenuIcon
                size={20}
                strokeWidth={1.5}
                style={{ color: theme.mode === "dark" ? "#e9edef" : "#1a1a1a" }}
              />
            </IconButton>
          )}
          {/* Título (desktop apenas) */}
          <Typography component="h2" variant="h6" color="inherit" noWrap className={classes.title}>
            {i18n.t("mainDrawer.appBar.user.message")} <b>{user.name}</b>,{" "}
            {i18n.t("mainDrawer.appBar.user.messageEnd")} <b>{user?.company?.name}</b>!
            {greaterThenSm && user?.profile === "admin" && user?.company?.dueDate && (
              <span className={classes.dueBadge}>
                <span className={classes.dueBadgeDot} />
                {i18n.t("mainDrawer.appBar.user.active")} {dateToClient(user?.company?.dueDate)}
              </span>
            )}
          </Typography>

          {/* Busca global — compartilhada por contatos/tickets/kanban via SearchContext */}
          {greaterThenSm && (
            <div className={classes.globalSearchWrap}>
              <TextField
                placeholder={searchPlaceholder}
                value={ticketSearchParam}
                onChange={(e) => setTicketSearchParam(e.target.value)}
                size="small"
                variant="outlined"
                fullWidth
                className={classes.globalSearchInput}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon size={16} strokeWidth={1.8} style={{ opacity: 0.6 }} />
                    </InputAdornment>
                  ),
                }}
              />
            </div>
          )}

          {greaterThenSm ? (
            /* ===== DESKTOP: todos os ícones visíveis ===== */
            <div className={classes.topbarScroller}>
              {userToken === "enabled" && user?.companyId === 1 && (
                <Chip className={classes.chip} label={i18n.t("mainDrawer.appBar.user.token")} />
              )}

              <VersionControl />
              <UserLanguageSelector />

              <ThemeToggle scale={0.75} />

              {user.id && <UnifiedNotifications />}

              <button
                type="button"
                className={classes.userTriggerBtn}
                onClick={handleMenu}
              >
                <Avatar
                  alt={user?.name || "user"}
                  className={classes.userTriggerAvatar}
                  src={profileUrl}
                />
                <span className={classes.userTriggerName}>
                  {user?.name || ""}
                </span>
                <ChevronDownIcon
                  size={14}
                  strokeWidth={2}
                  style={{ opacity: 0.55 }}
                />
              </button>
            </div>
          ) : (
            /* ===== MOBILE: avatar + menu 3 pontinhos ===== */
            <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginLeft: 'auto' }}>
              <IconButton
                onClick={() => window.location.reload()}
                style={{ padding: 6, color: theme.mode === "dark" ? "#e9edef" : "#1a1a1a" }}
              >
                <RefreshIcon
                  size={20}
                  strokeWidth={1.5}
                  style={{ color: theme.mode === "dark" ? "#e9edef" : "#1a1a1a" }}
                />
              </IconButton>

              {user.id && <UnifiedNotifications />}

              <button
                type="button"
                className={classes.userTriggerBtn}
                onClick={handleMenu}
                style={{ padding: 4 }}
              >
                <Avatar
                  alt={user?.name || "user"}
                  className={classes.userTriggerAvatar}
                  src={profileUrl}
                />
              </button>

              <IconButton
                onClick={(e) => setMoreMenuEl(e.currentTarget)}
                style={{ padding: 6, color: theme.mode === "dark" ? "#e9edef" : "#1a1a1a" }}
              >
                <MoreVertIcon
                  size={20}
                  strokeWidth={1.5}
                  style={{ color: theme.mode === "dark" ? "#e9edef" : "#1a1a1a" }}
                />
              </IconButton>

              <ChatiaPopover
                open={Boolean(moreMenuEl)}
                anchorEl={moreMenuEl}
                onClose={() => setMoreMenuEl(null)}
                title="Menu"
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              >
                {/* Idioma */}
                <div className={classes.moreMenuItem}>
                  <UserLanguageSelector />
                  <Typography className={classes.moreMenuLabel}>Idioma</Typography>
                </div>

                {/* Tema */}
                <div className={classes.moreMenuItem}>
                  <ThemeToggle scale={0.55} />
                  <Typography className={classes.moreMenuLabel}>
                    {theme.mode === "dark"
                      ? i18n.t("mainDrawer.appBar.themeLight")
                      : i18n.t("mainDrawer.appBar.themeDark")}
                  </Typography>
                </div>

                {/* Volume */}
                <div className={classes.moreMenuItem}>
                  <NotificationsVolume setVolume={setVolume} volume={volume} />
                  <Typography className={classes.moreMenuLabel}>
                    {i18n.t("mainDrawer.appBar.volume")}
                  </Typography>
                </div>


                {/* Instalar App (aparece quando não está instalado) */}
                {!isAppInstalled && (
                  <div
                    className={classes.moreMenuItem}
                    onClick={handleInstallPWA}
                    style={{ marginTop: 4, borderTop: `1px solid ${theme.mode === "dark" ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)"}`, borderRadius: 0, paddingTop: 12 }}
                  >
                    <IconButton>
                      <InstallIcon size={18} strokeWidth={1.5} />
                    </IconButton>
                    <Typography className={classes.moreMenuLabel}>
                      {i18n.t("mainDrawer.appBar.installPwa.title")}
                    </Typography>
                  </div>
                )}

                <VersionControl />
              </ChatiaPopover>
            </div>
          )}

          {/* Menu do usuário (compartilhado mobile/desktop) */}
          <UserModal
            open={userModalOpen}
            onClose={() => setUserModalOpen(false)}
            onImageUpdate={(newProfileUrl) => setProfileUrl(newProfileUrl)}
            userId={user?.id}
          />
          <ChatiaPopover
            open={menuOpen}
            anchorEl={anchorEl}
            onClose={handleCloseMenu}
            title={null}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
            paperStyle={{ minWidth: 260 }}
          >
            <div className={classes.userPopoverHeader}>
              <Avatar
                alt={user?.name || "user"}
                src={profileUrl}
                style={{ width: 38, height: 38 }}
              />
              <div style={{ display: "flex", flexDirection: "column", minWidth: 0, flex: 1 }}>
                <span className={classes.userPopoverHeaderName}>
                  {user?.name || ""}
                </span>
                {user?.email && (
                  <span className={classes.userPopoverHeaderEmail}>
                    {user.email}
                  </span>
                )}
              </div>
            </div>

            <div style={{ padding: "4px 8px 8px" }}>
              {/* Volume */}
              <div className={classes.userPopoverItem} style={{ cursor: "default" }}>
                <NotificationsVolume setVolume={setVolume} volume={volume} />
                <Typography style={{ fontSize: 13, fontWeight: 500 }}>
                  {i18n.t("mainDrawer.appBar.volumeNotifications")}
                </Typography>
              </div>

              <div className={classes.userPopoverDivider} />

              {/* Editar perfil */}
              <div
                className={classes.userPopoverItem}
                onClick={handleOpenUserModal}
              >
                <UserCogIcon size={18} strokeWidth={1.5} />
                <Typography style={{ fontSize: 13, fontWeight: 500 }}>
                  {i18n.t("mainDrawer.appBar.user.profile")}
                </Typography>
              </div>

              {/* Sair */}
              <div
                className={`${classes.userPopoverItem} ${classes.userPopoverItemDanger}`}
                onClick={handleClickLogout}
              >
                <LogOutIcon size={18} strokeWidth={1.5} />
                <Typography style={{ fontSize: 13, fontWeight: 600, color: "inherit" }}>
                  {i18n.t("mainDrawer.appBar.user.logout")}
                </Typography>
              </div>
            </div>
          </ChatiaPopover>

          {/* Instruções para instalar PWA */}
          <Dialog open={showPWAInstructions} onClose={() => setShowPWAInstructions(false)} maxWidth="xs">
            <DialogTitle>{i18n.t("mainDrawer.appBar.installPwa.title")}</DialogTitle>
            <DialogContent>
              {isIOS ? (
                <Typography variant="body2" component="div">
                  <ol style={{ paddingLeft: 20, margin: 0 }}>
                    <li style={{ marginBottom: 8 }}>{i18n.t("mainDrawer.appBar.installPwa.iosStep1")}</li>
                    <li style={{ marginBottom: 8 }}>{i18n.t("mainDrawer.appBar.installPwa.iosStep2")}</li>
                    <li>{i18n.t("mainDrawer.appBar.installPwa.iosStep3")}</li>
                  </ol>
                </Typography>
              ) : (
                <Typography variant="body2" component="div">
                  <ol style={{ paddingLeft: 20, margin: 0 }}>
                    <li style={{ marginBottom: 8 }}>{i18n.t("mainDrawer.appBar.installPwa.androidStep1")}</li>
                    <li style={{ marginBottom: 8 }}>{i18n.t("mainDrawer.appBar.installPwa.androidStep2")}</li>
                    <li>{i18n.t("mainDrawer.appBar.installPwa.androidStep3")}</li>
                  </ol>
                </Typography>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setShowPWAInstructions(false)} color="primary">
                {i18n.t("mainDrawer.appBar.installPwa.gotIt")}
              </Button>
            </DialogActions>
          </Dialog>
        </Toolbar>
      </AppBar>

      <main className={clsx(classes.content, drawerOpen && classes.contentShift)}>
        <div className={classes.appBarSpacer} />
        <div style={{ flex: 1, overflow: "hidden" }}>
          {children ? children : null}
        </div>
      </main>
      {showTrialWhatsAppButton && (
        <a
          href={trialWhatsAppUrl}
          target="_blank"
          rel="noopener noreferrer"
          className={classes.trialWhatsappButton}
          aria-label={trialWhatsAppText.label}
        >
          <span className={classes.trialWhatsappIcon}>
            <WhatsAppIcon />
          </span>
          <span>{trialWhatsAppText.label}</span>
        </a>
      )}
    </div>
  );
};

export default LoggedInLayout;
